import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, ChatCircle, Drop, UsersThree, FirstAidKit, Flask, Heartbeat, ListChecks, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../components/LoginModal';
import RomyIcon from '../components/RomyIcon';
import PageMeta from '../components/PageMeta';
import { apiUrl, buildAuthHeaders } from '../config/api';
import { getAttribution, getAnonId } from '../utils/attribution';
import { stashQuizAnswers, readQuizAnswers, seedProfileFromQuiz } from '../utils/quizHandoff';

// ─── Types ──────────────────────────────────────────────────────────

type SelectMode = 'single' | 'multi';

interface QuestionStep {
  type: 'question';
  id: string;
  title: string;
  subtitle?: string;
  options: string[];
  mode: SelectMode;
}

interface InsightStep {
  type: 'insight';
  getContent: (answers: Answers) => { emoji: string; headline: string; body: string };
}

interface ResultStep {
  type: 'result';
}

type Step = QuestionStep | InsightStep | ResultStep;

type Answers = Record<string, string[]>;

// ─── Data ───────────────────────────────────────────────────────────

const STEPS: Step[] = [
  {
    type: 'question',
    id: 'concern',
    title: "What's your #1 health concern right now?",
    options: [
      'Painful periods',
      'Irregular cycles',
      'Fertility',
      'Hormonal imbalance',
      'Chronic pain',
      'Perimenopause',
      'Just exploring',
    ],
    mode: 'single',
  },
  {
    type: 'question',
    id: 'duration',
    title: 'How long have you been dealing with this?',
    options: ['Less than 6 months', '6 months – 2 years', '2 – 5 years', '5+ years'],
    mode: 'single',
  },
  {
    type: 'insight',
    getContent: (answers) => {
      const concern = answers.concern?.[0] || '';
      const stats: Record<string, { emoji: string; headline: string; body: string }> = {
        'Painful periods': {
          emoji: '💡',
          headline: 'You\'re not imagining it.',
          body: 'Severe period pain affects 1 in 5 women — yet it takes an average of 7.5 years to get an endometriosis diagnosis. Romy helps you track patterns so you can advocate for real answers.',
        },
        'Irregular cycles': {
          emoji: '📊',
          headline: 'Cycles tell a story.',
          body: 'Irregular periods can signal hormonal shifts, thyroid issues, or conditions like PCOS. Tracking with Romy reveals patterns that one-off doctor visits miss.',
        },
        'Fertility': {
          emoji: '🌱',
          headline: 'Knowledge is power on this journey.',
          body: '1 in 6 people worldwide experience infertility. Romy helps you track key biomarkers and prepare smarter conversations with your fertility team.',
        },
        'Hormonal imbalance': {
          emoji: '⚖️',
          headline: 'Hormones run the show.',
          body: 'From acne to anxiety, hormonal shifts affect everything. 80% of women experience hormonal imbalance at some point. Romy connects the dots between your symptoms.',
        },
        'Chronic pain': {
          emoji: '🔬',
          headline: 'Your pain is real.',
          body: 'Women with chronic pain wait an average of 4 years longer than men for diagnosis. Romy helps you document patterns and prepare evidence-based conversations with providers.',
        },
        'Perimenopause': {
          emoji: '🌸',
          headline: 'The change before the change.',
          body: 'Perimenopause can start in your mid-30s, with 34+ recognized symptoms. Romy helps you identify what\'s hormonal vs. what needs further investigation.',
        },
      };
      return stats[concern] || {
        emoji: '✨',
        headline: 'Great place to start.',
        body: 'Understanding your health is a journey, not a destination. Romy uses AI and clinical research to help you make sense of your symptoms and feel confident in your care.',
      };
    },
  },
  {
    type: 'question',
    id: 'diagnoses',
    title: 'Have you been diagnosed with any of these?',
    subtitle: 'Select all that apply',
    options: [
      'Endometriosis',
      'PCOS',
      'PMDD',
      'Fibroids',
      'Adenomyosis',
      'Thyroid disorder',
      'Interstitial cystitis',
      'None of these',
    ],
    mode: 'multi',
  },
  {
    type: 'question',
    id: 'periods',
    title: 'How would you describe your periods?',
    options: ['Regular and manageable', 'Irregular or unpredictable', 'Heavy or painful', 'Absent or very light'],
    mode: 'single',
  },
  {
    type: 'question',
    id: 'symptoms',
    title: 'Which symptoms bother you the most?',
    subtitle: 'Select all that apply',
    options: [
      'Pelvic pain',
      'Bloating',
      'Fatigue',
      'Mood swings',
      'Brain fog',
      'Back pain',
      'Digestive issues',
      'Sleep problems',
    ],
    mode: 'multi',
  },
  {
    type: 'insight',
    getContent: (answers) => {
      const symptoms = answers.symptoms || [];
      const diagnoses = answers.diagnoses || [];
      const hasDiagnosis = diagnoses.length > 0 && !diagnoses.includes('None of these');

      if (symptoms.length >= 4) {
        return {
          emoji: '🧩',
          headline: 'Multiple symptoms often connect.',
          body: `You selected ${symptoms.length} symptoms — that\'s not random. Many women\'s health conditions share overlapping symptoms, making diagnosis harder. Romy cross-references your patterns with clinical research to surface insights your providers can act on.`,
        };
      }
      if (hasDiagnosis) {
        return {
          emoji: '🎯',
          headline: 'You know your body.',
          body: `Having a diagnosis is a starting point, not the finish line. Romy tracks how your ${diagnoses[0]} symptoms shift over time and alerts you to patterns worth discussing with your care team.`,
        };
      }
      return {
        emoji: '📈',
        headline: 'Data changes everything.',
        body: 'Even a few weeks of symptom tracking can reveal powerful patterns. Romy analyzes your logs to surface correlations you might not notice day-to-day.',
      };
    },
  },
  {
    type: 'question',
    id: 'tracking',
    title: 'How do you currently track your health?',
    options: ['Period tracking app', 'Notes or journal', 'I don\'t track anything', 'Spreadsheet or calendar'],
    mode: 'single',
  },
  {
    type: 'question',
    id: 'dismissed',
    title: 'Have you ever felt dismissed by a healthcare provider?',
    options: ['Yes, multiple times', 'Once or twice', 'No, I\'ve been heard', 'I avoid going to doctors'],
    mode: 'single',
  },
  {
    type: 'insight',
    getContent: (answers) => {
      const dismissed = answers.dismissed?.[0] || '';
      if (dismissed === 'Yes, multiple times' || dismissed === 'I avoid going to doctors') {
        return {
          emoji: '💪',
          headline: 'You deserve to be heard.',
          body: '72% of women report feeling dismissed by healthcare providers about their symptoms. Romy helps you prepare for appointments with organized symptom data, evidence-based questions, and advocacy letters so your voice can\'t be ignored.',
        };
      }
      if (dismissed === 'Once or twice') {
        return {
          emoji: '🤝',
          headline: 'Better conversations, better care.',
          body: 'Even one dismissive experience can shake your confidence. Romy\'s appointment prep tool helps you walk in with organized data and targeted questions — making it easier for providers to take you seriously.',
        };
      }
      return {
        emoji: '🌟',
        headline: 'That\'s great — let\'s keep it that way.',
        body: 'Having supportive providers makes all the difference. Romy helps you maximize those appointments by surfacing the most important patterns and questions to discuss.',
      };
    },
  },
  {
    type: 'question',
    id: 'age',
    title: 'What\'s your age range?',
    options: ['Under 25', '25–34', '35–44', '45–54', '55+'],
    mode: 'single',
  },
  {
    type: 'question',
    id: 'goals',
    title: 'What would help you the most?',
    subtitle: 'Select all that apply',
    options: [
      'Understanding my symptoms',
      'Preparing for doctor visits',
      'Connecting with others',
      'Tracking patterns over time',
      'Exploring treatment options',
      'Finding clinical trials',
    ],
    mode: 'multi',
  },
  {
    type: 'question',
    id: 'frequency',
    title: 'How often would you like Romy to check in?',
    options: ['Daily', 'A few times a week', 'Weekly', 'Only when I reach out'],
    mode: 'single',
  },
];

const TOTAL_QUESTIONS = STEPS.filter((s) => s.type === 'question').length;

// ─── Feature Recommendation Engine ─────────────────────────────────

interface FeatureRec {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
}

function getFeatureRecs(answers: Answers): FeatureRec[] {
  const concern = answers.concern?.[0] || '';
  const diagnoses = answers.diagnoses || [];
  const dismissed = answers.dismissed?.[0] || '';
  const goals = answers.goals || [];
  const tracking = answers.tracking?.[0] || '';
  const symptoms = answers.symptoms || [];
  const periods = answers.periods?.[0] || '';

  const hasDiagnosis = diagnoses.length > 0 && !diagnoses.includes('None of these');
  const wantsCommunity = goals.includes('Connecting with others');
  const wantsDoctorPrep = goals.includes('Preparing for doctor visits');
  const wantsTrials = goals.includes('Finding clinical trials');
  const wantsTracking = goals.includes('Tracking patterns over time');
  const wantsTreatments = goals.includes('Exploring treatment options');
  const doesntTrack = tracking === "I don't track anything";
  const wasDismissed = dismissed === 'Yes, multiple times' || dismissed === 'Once or twice' || dismissed === 'I avoid going to doctors';
  const hasCycleConcern = concern === 'Painful periods' || concern === 'Irregular cycles' || periods === 'Heavy or painful' || periods === 'Irregular or unpredictable';
  const hasSymptoms = symptoms.length > 0;

  const recs: FeatureRec[] = [];

  // Always recommend chat
  recs.push({
    icon: <ChatCircle size={24} weight="fill" />,
    title: 'Chat with Romy',
    description: 'Your AI health companion — ask anything, track symptoms conversationally, get personalized insights.',
    path: '/chat',
  });

  if (hasCycleConcern || hasSymptoms) {
    recs.push({
      icon: <Drop size={24} weight="fill" />,
      title: 'Cycle Tracker',
      description: 'Visualize your cycle, predict your next period, and spot patterns across phases.',
      path: '/health/cycle',
    });
  }

  if (hasDiagnosis || wantsCommunity) {
    recs.push({
      icon: <UsersThree size={24} weight="fill" />,
      title: 'Community',
      description: 'Connect with others who understand. Share experiences, ask questions, find support.',
      path: '/community',
    });
  }

  if (wasDismissed || wantsDoctorPrep) {
    recs.push({
      icon: <FirstAidKit size={24} weight="fill" />,
      title: 'Appointment Prep',
      description: 'Walk into your next appointment with organized data, targeted questions, and confidence.',
      path: '/health/appointments',
    });
  }

  if (wantsTrials) {
    recs.push({
      icon: <Flask size={24} weight="fill" />,
      title: 'Clinical Trial Finder',
      description: 'Discover clinical trials matched to your conditions and location.',
      path: '/trials',
    });
  }

  if (wantsTracking || doesntTrack) {
    recs.push({
      icon: <Heartbeat size={24} weight="fill" />,
      title: 'Health Log',
      description: 'Log symptoms, medications, and mood. Romy finds the patterns you miss.',
      path: '/health',
    });
  }

  if (wantsTreatments || hasDiagnosis) {
    recs.push({
      icon: <ListChecks size={24} weight="fill" />,
      title: 'Treatment Timeline',
      description: 'Visualize your treatment journey — medications, supplements, procedures — all in one place.',
      path: '/health/treatments',
    });
  }

  return recs.slice(0, 4);
}

// ─── Result Summary ────────────────────────────────────────────────

function getProfileSummary(answers: Answers): string[] {
  const chips: string[] = [];
  if (answers.concern?.[0]) chips.push(answers.concern[0]);
  if (answers.duration?.[0]) chips.push(answers.duration[0]);
  const diagnoses = (answers.diagnoses || []).filter((d) => d !== 'None of these');
  if (diagnoses.length) chips.push(...diagnoses.slice(0, 2));
  if (answers.age?.[0]) chips.push(answers.age[0]);
  return chips;
}

// ─── Component ──────────────────────────────────────────────────────

export default function QuizFunnel() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [pendingSelection, setPendingSelection] = useState<Set<string>>(new Set());
  const [history, setHistory] = useState<number[]>([]);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [showLogin, setShowLogin] = useState(false);
  const [animating, setAnimating] = useState(false);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Signing in from the CTA flips App's <Routes key={uid}>, remounting this
  // whole tree — so an in-place navigate after login never fires. We instead
  // stash a redirect intent before opening login and honor it on the fresh
  // mount (works whether or not the remount happens).
  const REDIRECT_KEY = 'quiz_post_login_redirect';
  const readRedirect = () => {
    try { return sessionStorage.getItem(REDIRECT_KEY) === '1'; } catch { return false; }
  };
  const clearRedirect = () => {
    try { sessionStorage.removeItem(REDIRECT_KEY); } catch { /* ignore */ }
  };
  const [postLoginRedirect, setPostLoginRedirect] = useState(() => !!currentUser && readRedirect());

  useEffect(() => {
    if (currentUser && !postLoginRedirect && readRedirect()) {
      setPostLoginRedirect(true);
    }
    if (postLoginRedirect) clearRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, postLoginRedirect]);

  // Once authed (including after a signup remount), seed the profile from the
  // stashed quiz answers. Fire-and-forget; the stash is kept so Intake can also
  // pre-fill from it. Guarded so it only runs once per mount.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!currentUser || seededRef.current) return;
    const stashed = readQuizAnswers();
    if (stashed) {
      seededRef.current = true;
      seedProfileFromQuiz(stashed).catch(() => { /* best-effort */ });
    }
  }, [currentUser]);

  const step = currentIndex < STEPS.length ? STEPS[currentIndex] : { type: 'result' as const };

  // Count which question number we're on (for progress bar)
  const questionIndex = STEPS.slice(0, currentIndex).filter((s) => s.type === 'question').length;

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const goForward = useCallback(
    (nextIndex: number) => {
      setDirection('forward');
      setHistory((prev) => [...prev, currentIndex]);
      setAnimating(true);
      // Small delay for animation to register
      requestAnimationFrame(() => {
        setCurrentIndex(nextIndex);
        setPendingSelection(new Set());
        // Allow animation class to apply
        setTimeout(() => setAnimating(false), 350);
      });
    },
    [currentIndex],
  );

  const goBack = useCallback(() => {
    if (history.length === 0) {
      navigate(-1);
      return;
    }
    setDirection('back');
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setAnimating(true);
    requestAnimationFrame(() => {
      setCurrentIndex(prev);
      // Restore previous answer as pending selection for multi-select
      const prevStep = STEPS[prev];
      if (prevStep?.type === 'question') {
        const prevAnswer = answers[prevStep.id];
        setPendingSelection(new Set(prevAnswer || []));
      } else {
        setPendingSelection(new Set());
      }
      setTimeout(() => setAnimating(false), 350);
    });
  }, [history, navigate, answers]);

  const handleSingleSelect = useCallback(
    (option: string) => {
      if (animating) return;
      const q = step as QuestionStep;
      // Highlight then auto-advance
      setPendingSelection(new Set([option]));
      setAnswers((prev) => ({ ...prev, [q.id]: [option] }));

      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = setTimeout(() => {
        goForward(currentIndex + 1);
      }, 350);
    },
    [step, currentIndex, goForward, animating],
  );

  const handleMultiToggle = useCallback(
    (option: string) => {
      setPendingSelection((prev) => {
        const next = new Set(prev);
        // "None of these" logic: selecting it clears everything else
        if (option === 'None of these') {
          return next.has(option) ? new Set() : new Set([option]);
        }
        // Selecting anything else clears "None of these"
        next.delete('None of these');
        if (next.has(option)) next.delete(option);
        else next.add(option);
        return next;
      });
    },
    [],
  );

  const handleMultiNext = useCallback(() => {
    if (pendingSelection.size === 0 || animating) return;
    const q = step as QuestionStep;
    setAnswers((prev) => ({ ...prev, [q.id]: [...pendingSelection] }));
    goForward(currentIndex + 1);
  }, [pendingSelection, step, currentIndex, goForward, animating]);

  const handleCTA = useCallback(() => {
    // Pass quiz answers through the (possible) signup remount so the authed side
    // can seed the profile + pre-fill intake. Stash in both branches.
    stashQuizAnswers(answers);
    if (currentUser) {
      seedProfileFromQuiz(answers).catch(() => { /* best-effort */ });
      navigate('/chat');
    } else {
      // Remember to land them in chat once sign-in completes (survives remount).
      try { sessionStorage.setItem(REDIRECT_KEY, '1'); } catch { /* ignore */ }
      setShowLogin(true);
    }
  }, [currentUser, navigate, answers]);

  const handleLoginClose = useCallback(() => {
    setShowLogin(false);
    if (currentUser) {
      clearRedirect();
      navigate('/chat');
    } else {
      // Cancelled without signing in — drop the stashed redirect intent.
      clearRedirect();
    }
  }, [currentUser, navigate]);

  const animationClass = animating
    ? 'opacity-0'
    : direction === 'forward'
      ? 'animate-slideInRight'
      : 'animate-slideInLeft';

  // Signed in via the funnel CTA → go straight to chat (no flash of question 1).
  if (postLoginRedirect) return <Navigate to="/chat" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-pink-50 to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 flex flex-col">
      <PageMeta
        title="Get Started — Personalized Health Profile | Romy"
        description="Take a 2-minute quiz to get personalized women's health insights, feature recommendations, and connect with Romy, your AI health companion."
        path="/get-started"
      />

      {/* Progress bar + back button */}
      {step.type !== 'result' && (
        <div className="fixed top-0 left-0 right-0 z-20 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <div className="flex items-center px-4 py-3 max-w-lg mx-auto">
            <button
              onClick={goBack}
              className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={22} className="text-gray-600 dark:text-gray-400" />
            </button>
            <div className="flex-1 mx-3">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${((questionIndex + (step.type === 'question' ? 0 : 0)) / TOTAL_QUESTIONS) * 100}%` }}
                />
              </div>
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 min-w-[3rem] text-right">
              {step.type === 'question' ? `${questionIndex + 1}/${TOTAL_QUESTIONS}` : ''}
            </span>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 pt-16 pb-8">
        <div className={`w-full max-w-lg ${animationClass}`}>
          {/* ─── Question Step ─── */}
          {step.type === 'question' && (
            <QuestionScreen
              step={step as QuestionStep}
              pendingSelection={pendingSelection}
              onSingleSelect={handleSingleSelect}
              onMultiToggle={handleMultiToggle}
              onMultiNext={handleMultiNext}
            />
          )}

          {/* ─── Insight Step ─── */}
          {step.type === 'insight' && (
            <InsightScreen
              content={(step as InsightStep).getContent(answers)}
              onContinue={() => goForward(currentIndex + 1)}
            />
          )}

          {/* ─── Result Step ─── */}
          {(step.type === 'result' || currentIndex >= STEPS.length) && (
            <ResultScreen
              answers={answers}
              onCTA={handleCTA}
              isSignedIn={!!currentUser}
            />
          )}
        </div>
      </div>

      <LoginModal isOpen={showLogin} onClose={handleLoginClose} />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function QuestionScreen({
  step,
  pendingSelection,
  onSingleSelect,
  onMultiToggle,
  onMultiNext,
}: {
  step: QuestionStep;
  pendingSelection: Set<string>;
  onSingleSelect: (option: string) => void;
  onMultiToggle: (option: string) => void;
  onMultiNext: () => void;
}) {
  return (
    <div className="flex flex-col">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2 leading-tight">
        {step.title}
      </h2>
      {step.subtitle && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{step.subtitle}</p>
      )}
      {!step.subtitle && <div className="mb-6" />}

      <div className="flex flex-wrap gap-3">
        {step.options.map((option) => {
          const selected = pendingSelection.has(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => (step.mode === 'single' ? onSingleSelect(option) : onMultiToggle(option))}
              className={`px-5 py-3 text-sm sm:text-base rounded-xl border-2 transition-all duration-200 ${
                selected
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white border-transparent shadow-lg scale-[1.02]'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-500 hover:shadow-md'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>

      {step.mode === 'multi' && (
        <button
          onClick={onMultiNext}
          disabled={pendingSelection.size === 0}
          className="mt-8 w-full px-6 py-3.5 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm disabled:shadow-none"
        >
          Next
        </button>
      )}
    </div>
  );
}

function InsightScreen({
  content,
  onContinue,
}: {
  content: { emoji: string; headline: string; body: string };
  onContinue: () => void;
}) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="text-5xl mb-5">{content.emoji}</div>
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4 leading-tight">
        {content.headline}
      </h2>
      <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-md mb-8">
        {content.body}
      </p>
      <button
        onClick={onContinue}
        className="px-8 py-3.5 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600 transition-all shadow-sm"
      >
        Continue
      </button>
    </div>
  );
}

function ResultScreen({
  answers,
  onCTA,
  isSignedIn,
}: {
  answers: Answers;
  onCTA: () => void;
  isSignedIn: boolean;
}) {
  const chips = getProfileSummary(answers);
  const recs = getFeatureRecs(answers);

  // Persist a structured snapshot of this funnel run for product analytics —
  // fires once for every completer (anonymous, email-only, or signed-in), so we
  // capture areas of interest / demographics even when no email is submitted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await buildAuthHeaders();
        if (cancelled) return;
        await fetch(apiUrl('/quiz-response'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            answers,
            source: 'quiz_funnel',
            anonId: getAnonId(),
            attribution: getAttribution(),
          }),
          keepalive: true,
        });
      } catch {
        /* best-effort analytics — ignore failures */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally fire once on mount with the final answer set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    setEmailStatus('loading');
    setEmailError('');

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };

      const res = await fetch(apiUrl('/subscribe'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: trimmed, source: 'quiz_funnel', answers, attribution: getAttribution() }),
      });

      const data = await res.json();
      if (data.success) {
        setEmailStatus('success');
      } else {
        setEmailError("That didn't go through — let's try again.");
        setEmailStatus('error');
      }
    } catch {
      setEmailError("That didn't go through — let's try again.");
      setEmailStatus('error');
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* Header */}
      <div className="text-pink-500 mb-4">
        <RomyIcon size={56} weight="fill" />
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2 text-center leading-tight">
        Your Health Profile
      </h2>
      <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-6 text-center">
        Here's what Romy can do for you
      </p>

      {/* Summary chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {chips.map((chip) => (
            <span
              key={chip}
              className="px-3 py-1.5 text-xs font-medium rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border border-pink-200 dark:border-pink-800"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {/* Feature recommendations */}
      <div className="w-full space-y-3 mb-8">
        {recs.map((rec) => (
          <div
            key={rec.title}
            className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 flex items-center justify-center text-pink-500">
              {rec.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{rec.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{rec.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Email capture */}
      {!isSignedIn && (
        <div className="w-full mb-6">
          {emailStatus === 'success' ? (
            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <CheckCircle size={20} weight="fill" className="text-green-500" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Check your inbox!</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-2">
                Get your health profile + tips via email
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="your@email.com"
                    className="w-full pl-9 pr-3 py-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 dark:focus:ring-pink-600 placeholder:text-gray-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailStatus === 'loading'}
                  className="px-5 py-3 bg-pink-500 text-white text-sm font-semibold rounded-xl hover:bg-pink-600 transition-all disabled:opacity-60 whitespace-nowrap"
                >
                  {emailStatus === 'loading' ? 'Sending...' : 'Send My Profile'}
                </button>
              </div>
              {emailError && (
                <p className="text-xs text-red-500 dark:text-red-400 text-center">{emailError}</p>
              )}
            </form>
          )}
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onCTA}
        className="w-full px-6 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg text-base"
      >
        {isSignedIn ? 'Start Chatting with Romy' : 'Create Free Account'}
      </button>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 text-center">
        Free forever. No credit card required.
      </p>
      {!isSignedIn && (
        <Link
          to="/"
          className="mt-4 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          Maybe later — let me explore first
        </Link>
      )}
    </div>
  );
}
